import React, { useState } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { AdvancedLifeAnalysisResult, LifeThemeId } from '../../types/advancedLifeAnalysis';
import { assignRemedies, PURUSHARTHA_SAFEGUARD } from '../../utils/remediesBankEngine';
import { GemstoneAdvisor } from '../GemstoneAdvisor';
import {
  Sparkles,
  Shield,
  Heart,
  Sun,
  Moon,
  Gem,
  Compass,
  HandHeart,
  BookOpen
} from 'lucide-react';

interface HarmonizedRemediesHubProps {
  kundali: CompleteKundali;
  analysisResult: AdvancedLifeAnalysisResult;
  selectedThemeId: LifeThemeId;
  lang: Language;
}

export const HarmonizedRemediesHub: React.FC<HarmonizedRemediesHubProps> = ({
  kundali,
  analysisResult,
  selectedThemeId,
  lang
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'mantras' | 'gemstone' | 'daily_daan' | 'theme_remedies'>('mantras');
  const [showFullAdvisor, setShowFullAdvisor] = useState(false);

  const { gemstones: kundaliGems } = kundali;
  const lifeGem = kundaliGems?.find((g) => g.type === 'Life Stone (Lagna)') || kundaliGems?.[0] || {
    gemstone: 'Yellow Sapphire / Pukhraj',
    substitute: 'Yellow Topaz',
    finger: 'Index Finger',
    metal: 'Gold / Brass',
    wearingDay: 'Thursday morning'
  };

  const assigned = assignRemedies(kundali, selectedThemeId, 'BASIC', lang);
  const primaryRemedies = assigned.filter((a) => a.rank === 'PRIMARY');

  return (
    <div
      id="harmonized-remedies-hub"
      className="bg-astro-bg border border-astro-border rounded-2xl p-5 sm:p-7 shadow-xs space-y-6"
    >
      {/* 1. Header with Title */}
      <div className="border-b border-astro-border pb-4 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-astro-primary/10 flex items-center justify-center text-astro-primary">
            <Shield className="w-4 h-4 text-astro-accent" />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full">
            {isHi ? 'वैदिक उपचारात्मक समन्वय' : 'Harmonized Vedic Wellness & Remedies'}
          </span>
        </div>
        <h3 className="text-lg sm:text-xl font-bold font-serif text-astro-text">
          {isHi ? 'शास्त्रीय उपाय, मंत्र साधना एवं दैनिक कल्याण' : 'Classical Remedies, Sacred Mantras & Daily Balance'}
        </h3>
        <p className="text-xs text-astro-secondary-text">
          {isHi
            ? 'आपकी जन्म कुंडली व वर्तमान समय चक्र के अनुकूल प्रमाणिक एवं सरल वैदिक मार्गदर्शन'
            : 'Authentic, practical Vedic guidance aligned with your planetary strengths and life stage'}
        </p>
      </div>

      {/* Universal Classical Purushartha Non-Guarantee Safeguard Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 shadow-2xs flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="space-y-1 text-xs">
          <h4 className="font-bold text-amber-900 dark:text-amber-200">
            {isHi ? PURUSHARTHA_SAFEGUARD.hi.title : PURUSHARTHA_SAFEGUARD.en.title}
          </h4>
          <p className="text-amber-800/90 dark:text-amber-300/90 leading-relaxed">
            {isHi ? PURUSHARTHA_SAFEGUARD.hi.text : PURUSHARTHA_SAFEGUARD.en.text}
          </p>
        </div>
      </div>

      {/* 2. Navigation Tabs */}
      <div className="w-full overflow-x-auto custom-horizontal-scrollbar pb-1 border-b border-astro-border/70">
        <div className="flex items-center gap-2 min-w-max px-0.5">
          <button
            type="button"
            onClick={() => setActiveTab('mantras')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'mantras'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-astro-secondary-text hover:text-astro-text border border-astro-border'
            }`}
          >
            <Moon className={`w-3.5 h-3.5 ${activeTab === 'mantras' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
            <span>{isHi ? '१. वैदिक मंत्र साधना' : '1. Sacred Vedic Mantras'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('gemstone')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'gemstone'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-astro-secondary-text hover:text-astro-text border border-astro-border'
            }`}
          >
            <Gem className={`w-3.5 h-3.5 ${activeTab === 'gemstone' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
            <span>{isHi ? '२. भाग्य रत्न व धातु' : '2. Auspicious Gem & Metal'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('daily_daan')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'daily_daan'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-astro-secondary-text hover:text-astro-text border border-astro-border'
            }`}
          >
            <HandHeart className={`w-3.5 h-3.5 ${activeTab === 'daily_daan' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
            <span>{isHi ? '३. दैनिक क्रियाएं एवं शुभ दान' : '3. Daily Rituals & Charity (Daan)'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('theme_remedies')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'theme_remedies'
                ? 'bg-astro-primary text-white shadow-2xs'
                : 'bg-[#FAF6ED] text-astro-secondary-text hover:text-astro-text border border-astro-border'
            }`}
          >
            <Sparkles className={`w-3.5 h-3.5 ${activeTab === 'theme_remedies' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
            <span>{isHi ? '४. विषयगत विशेष उपाय' : '4. Domain-Specific Remedies'}</span>
          </button>
        </div>
      </div>

      {/* 3. Tab Content Panels */}
      <div>
        {/* Tab 1: Mantras */}
        {activeTab === 'mantras' && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Daily Lagna Peace Mantra */}
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <Moon className="w-4 h-4 text-astro-accent" />
                    <span>{isHi ? 'लग्न शांति एवं आत्मबल मंत्र' : 'Lagna Peace & Vitality Mantra'}</span>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-astro-primary/10 text-astro-primary">
                    {isHi ? 'दैनिक स्मरण' : 'Daily Recitation'}
                  </span>
                </div>
                <div className="p-3 bg-astro-bg rounded-lg border border-astro-border text-center">
                  <p className="text-sm sm:text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ॐ नमः शिवाय' : 'Om Namah Shivaya'}
                  </p>
                  <p className="text-[11px] text-astro-secondary-text mt-1 font-mono">
                    {isHi ? '१०८ बार शांत चित्त से प्रातः स्मरण करें' : 'Recite 108 times calmly in the morning'}
                  </p>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'यह मूल मंत्र आपके लग्न व मन को एकाग्रता, आत्म-शांति एवं नकारात्मक ऊर्जा से सुरक्षा प्रदान करता है।'
                    : 'This foundational mantra grounds mental clarity, enhances resilience, and fosters inner peace.'}
                </p>
              </div>

              {/* Guiding Planetary Chanting */}
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <Sun className="w-4 h-4 text-astro-accent" />
                    <span>{isHi ? 'मार्गदर्शक ग्रहीय मंत्र' : 'Guiding Planetary Mantra'}</span>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-astro-accent/20 text-astro-text">
                    {isHi ? 'ग्रह बल' : 'Planetary Vitality'}
                  </span>
                </div>
                <div className="p-3 bg-astro-bg rounded-lg border border-astro-border text-center">
                  <p className="text-sm sm:text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ॐ सूर्याय नमः / ॐ बृं बृहस्पतये नमः' : 'Om Suryaya Namah / Om Brihaspataye Namah'}
                  </p>
                  <p className="text-[11px] text-astro-secondary-text mt-1 font-mono">
                    {isHi ? 'प्रातः सूर्योदय अथवा गुरुवार को स्मरण' : 'Morning sunrise or Thursday chanting'}
                  </p>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'प्रमुख शुभ ग्रहों के मंत्र स्मरण से बौद्धिक प्रखरता, निर्णय क्षमता एवं आत्मविश्वास का संवर्धन होता है।'
                    : 'Chanting planetary seed sounds aligns vital cosmic frequencies, improving executive focus and clarity.'}
                </p>
              </div>

              {/* Gayatri / Universal Grace Mantra */}
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4 text-astro-accent" />
                    <span>{isHi ? 'गायत्री महामंत्र (सद्बुद्धि व तेज)' : 'Gayatri Mahamantra (Wisdom & Radiance)'}</span>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-astro-primary/10 text-astro-primary">
                    {isHi ? 'संध्या व प्रातः' : 'Dawn & Dusk'}
                  </span>
                </div>
                <div className="p-3 bg-astro-bg rounded-lg border border-astro-border text-center">
                  <p className="text-sm sm:text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यं भर्गो देवस्य धीमहि धियो यो नः प्रचोदयात्॥' : 'Om Bhur Bhuvah Swah Tat-Savitur Varenyam Bhargo Devasya Dheemahi Dhiyo Yo Nah Prachodayat'}
                  </p>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'ऋग्वेद का यह दिव्य मंत्र बुद्धि को शुद्ध, एकाग्र और सन्मार्ग पर प्रेरित करता है।'
                    : 'This timeless Vedic prayer purifies intellect, illuminates consciousness, and aligns personal effort with divine wisdom.'}
                </p>
              </div>

              {/* Mahamrityunjaya Shield Mantra */}
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-astro-accent" />
                    <span>{isHi ? 'महामृत्युंजय मंत्र (आरोग्य एवं सुरक्षा)' : 'Mahamrityunjaya Mantra (Health & Protection)'}</span>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800">
                    {isHi ? 'रक्षा कवच' : 'Protective Shield'}
                  </span>
                </div>
                <div className="p-3 bg-astro-bg rounded-lg border border-astro-border text-center">
                  <p className="text-sm sm:text-base font-bold font-serif text-astro-primary">
                    {isHi ? 'ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम्। उर्वारुकमिव बन्धनान्मृत्योर्मुक्षीय माऽमृतात्॥' : 'Om Tryambakam Yajamahe Sugandhim Pushti-Vardhanam Urvarukamiva Bandhanan Mrityor Mukshiya Mamritat'}
                  </p>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'समस्त प्रकार के भय, व्याधि और आकस्मिक संकटों से सुरक्षा हेतु अत्यंत प्रभावशाली स्तोत्र।'
                    : 'Invokes deep rejuvenation, robust health, and spiritual resilience against distress and anxieties.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Gemstones & Metals */}
        {activeTab === 'gemstone' && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                    <Gem className="w-4 h-4 text-astro-accent" />
                    <span>{isHi ? 'मुख्य भाग्य रत्न (लग्न रत्न)' : 'Primary Life Gemstone (Lagna Ratna)'}</span>
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-astro-primary text-white">
                    {isHi ? 'शुभ' : 'Auspicious'}
                  </span>
                </div>
                <div className="space-y-1.5 text-xs text-astro-text">
                  <div className="flex justify-between py-1 border-b border-astro-border">
                    <span className="text-astro-secondary-text">{isHi ? 'अनुशंसित रत्न:' : 'Recommended Gem:'}</span>
                    <strong className="text-astro-primary font-bold">{lifeGem.gemstone}</strong>
                  </div>
                  <div className="flex justify-between py-1 border-b border-astro-border">
                    <span className="text-astro-secondary-text">{isHi ? 'उपरत्न / विकल्प:' : 'Substitute:'}</span>
                    <span>{lifeGem.substitute || 'Topaz / Amber'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-astro-border">
                    <span className="text-astro-secondary-text">{isHi ? 'उचित धातु:' : 'Metal:'}</span>
                    <span>{lifeGem.metal || 'Gold / Brass'}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-astro-border">
                    <span className="text-astro-secondary-text">{isHi ? 'उंगली:' : 'Finger:'}</span>
                    <span>{lifeGem.finger || 'Index / Ring Finger'}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-astro-secondary-text">{isHi ? 'धारण का शुभ समय:' : 'Auspicious Day/Time:'}</span>
                    <span>{'wearingDay' in lifeGem ? lifeGem.wearingDay : 'Thursday morning (Shukla Paksha)'}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary">
                  <Compass className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'रत्न शुद्धि एवं धारण विधि' : 'Energization & Cleansing Rules'}</span>
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'रत्न धारण करने से पूर्व उसे गंगाजल एवं कच्चे दूध से शुद्ध करें। इसके पश्चात संबंधित ग्रह मंत्र का १०८ बार जाप करके ही शुभ मुहूर्त में धारण करें।'
                    : 'Before wearing any sacred gemstone, cleanse it gently with pure water or milk. Energize by reciting the planetary mantra 108 times during the waxing moon phase.'}
                </p>
                <div className="p-3 bg-astro-bg rounded-lg border border-astro-border text-[11px] text-astro-secondary-text space-y-1">
                  <span className="font-bold text-astro-primary block">
                    {isHi ? 'महत्वपूर्ण शास्त्रीय निर्देश:' : 'Important Classical Note:'}
                  </span>
                  <span>
                    {isHi
                      ? 'अशुभ या मारक ग्रहों के रत्न बिना विद्वान परामर्श के कभी धारण न करें। लग्न व त्रिकोण के रत्न ही सदैव सुरक्षित व फलदायी होते हैं।'
                      : 'Always wear gemstones of functional benefics (1st, 5th, 9th houses). Never wear stones of dusthana lords without expert guidance.'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowFullAdvisor((prev) => !prev);
                    window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'gemstones' }));
                  }}
                  className="w-full mt-2 py-2.5 px-3 bg-astro-primary text-white text-xs font-semibold rounded-lg hover:bg-astro-primary/90 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                >
                  <Gem className="w-3.5 h-3.5 text-astro-accent" />
                  <span>
                    {showFullAdvisor
                      ? (isHi ? 'रत्न परामर्श छुपाएं ▴' : 'Hide Gemstone Advisor ▴')
                      : (isHi ? 'विस्तृत ११-बिंदु रत्न परामर्श खोलें →' : 'Launch Full 11-Factor Gemstone Advisor →')}
                  </span>
                </button>
              </div>
            </div>

            {/* In-place Full Gemstone Advisor Container */}
            {showFullAdvisor && (
              <div className="mt-4 pt-4 border-t border-astro-border animate-fade-in">
                <GemstoneAdvisor kundali={kundali} lang={lang} />
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Daily Rituals & Charity (Daan) */}
        {activeTab === 'daily_daan' && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2">
                <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                  <Sun className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? '१. प्रातः सूर्य अर्घ्य' : '1. Morning Solar Arghya'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'तांबे के पात्र में शुद्ध जल, अक्षत व रोली लेकर उगते सूर्य को अर्घ्य दें। यह आत्मबल, आत्मविश्वास और स्वास्थ्य में वृद्धि करता है।'
                    : 'Offer pure water to the rising Sun in a copper vessel in the morning to enhance vitality, immune strength, and executive clarity.'}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2">
                <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                  <HandHeart className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? '२. शुभ दान एवं जीव सेवा' : '2. Auspicious Giving (Daan)'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'सप्ताह में एक दिन पक्षियों को दाना, गाय को चारा अथवा किसी जरूरतमंद की गुप्त सहायता करें। इससे ग्रहों की प्रतिकूलता समाप्त होती है।'
                    : 'Feed birds, support animals, or provide discreet assistance to people in need on your favorable day to balance karmic energies.'}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2">
                <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-astro-primary" />
                  <span>{isHi ? '३. मानसिक संतुलन व दिनचर्या' : '3. Balanced Lifestyle'}</span>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi
                    ? 'प्राणायाम, संतुलित आहार और रात्रि में समय पर शयन आपके त्रिदोषों (वात-पित्त-कफ) को संतुलित रखकर ऊर्जावान बनाए रखता है।'
                    : 'Gentle pranayama, clean nutrition, and consistent sleep rhythms harmonize bio-energies and maintain consistent productivity.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Domain-Specific Remedies for Active Selected Question */}
        {activeTab === 'theme_remedies' && (
          <div className="space-y-4 animate-fade-in">
            <div className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'इस जीवन क्षेत्र हेतु विशेष अनुशंसित उपाय:' : 'Recommended Practices for Selected Area:'}</span>
            </div>

            {primaryRemedies.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {primaryRemedies.map((cand, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border space-y-2.5"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-astro-primary/10 text-astro-primary">
                        {cand.remedy.category}
                      </span>
                      <span className="text-xs font-bold text-astro-accent">
                        {cand.remedy.planet}
                      </span>
                    </div>
                    <h4 className="text-sm font-bold text-astro-text">
                      {isHi ? cand.remedy.nameHi : cand.remedy.name}
                    </h4>
                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {isHi ? cand.whyThisRemedyHi : cand.whyThisRemedy}
                    </p>
                    <div className="text-[11px] text-astro-primary font-medium bg-astro-bg p-2 rounded-lg border border-astro-border">
                      {isHi ? (cand.remedy.explanationHi || cand.remedy.frequency) : (cand.remedy.explanation || cand.remedy.frequency)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-border text-center text-xs text-astro-secondary-text">
                {isHi
                  ? 'इस जीवन क्षेत्र के लिए सामान्य वैदिक मंत्र व दैनिक दान पर्याप्त व सर्वहितकारी हैं।'
                  : 'Daily solar arghya, peace mantras, and regular charity are universally beneficial for this domain.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
